import {
  type PanInfo,
  useAnimation,
  useDragControls,
  useMotionValue,
} from "motion/react";
import {
  type PointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { useIsPositionsDrawerDragged } from "#/store/app/positionsDrawer/positionsDrawerDragged";
import { usePositionsDrawerSettings } from "#/store/app/positionsDrawer/positionsDrawerSettings";
import type { PositionsDrawerTab } from "#/store/app/positionsDrawer/positionsDrawerTab";
import { usePositionsDrawerTab } from "#/store/app/positionsDrawer/positionsDrawerTab";

const MIN_DRAWER_HEIGHT = 50;

const getDefaultDrawerHeight = () => window.innerHeight / 4;
const getMaxDrawerHeight = () => window.innerHeight / 2;

export const useDrawerDrag = () => {
  const dragControls = useDragControls();
  const animation = useAnimation();
  const drawerRef = useRef<HTMLElement>(null);
  const { positionsDrawerSettings, setPositionsDrawerSettings } =
    usePositionsDrawerSettings();
  const { setIsPositionsDrawerDragged } = useIsPositionsDrawerDragged();
  const { positionsDrawerTab, setPositionsDrawerTab } = usePositionsDrawerTab();
  const [drawerHeight, setDrawerHeight] = useState(getDefaultDrawerHeight());
  const [contentHeight, setContentHeight] = useState(getDefaultDrawerHeight());
  const top = useMotionValue(getDefaultDrawerHeight());

  const adjustContentDimensions = () => {
    const height = getMaxDrawerHeight();
    const currentY = drawerRef.current!.getBoundingClientRect().y;

    setContentHeight(window.innerHeight - currentY);

    top.set(Math.round(Math.max(currentY - height, 0)));
  };

  const calculateShowRatio = () => {
    const height = getMaxDrawerHeight();
    const showRatio =
      1 - Math.min(positionsDrawerSettings.currentHeight, height) / height;
    const showRatioPercentage = Math.round(showRatio * 100);

    return { showRatio, showRatioPercentage };
  };

  /**
   * Recalculates drawer height and position based on new window dimensions
   * Maintains the relative position of the drawer when window is resized
   * And recalculates the inner content height as well in order to maintain
   * it's size relative to the parent's size.
   */
  const handleResize = useCallback(() => {
    const newHeight = getMaxDrawerHeight();
    const newPosition = Math.min(
      Math.max(0, newHeight * positionsDrawerSettings.ratio),
      newHeight,
    );

    const newCurrentHeight = Math.abs(
      Math.max(0, newHeight * (1 - positionsDrawerSettings.ratio)),
    );

    setDrawerHeight(newHeight);
    newCurrentHeight > 0 &&
      setPositionsDrawerSettings({
        currentHeight: newCurrentHeight,
      });

    animation.set({ y: newPosition });

    setTimeout(adjustContentDimensions);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- no need to re-run on other props changes
  }, [animation, positionsDrawerSettings.ratio]);

  const handleToggle = (shouldToggleFullHeight: boolean) => {
    const show = !positionsDrawerSettings.show;

    if (shouldToggleFullHeight) {
      setPositionsDrawerSettings({
        show,
        ratio: show ? 0 : 1,
        currentHeight: getMaxDrawerHeight(),
      });
      return;
    }

    const { showRatio } = calculateShowRatio();

    setPositionsDrawerSettings({
      show,
      ratio: show ? showRatio : 1,
    });

    // defer the animation start
  };

  const handleDrag = (e: PointerEvent<HTMLButtonElement>) => {
    dragControls.start(e);
    setIsPositionsDrawerDragged(true);
  };

  /**
   * Flip the toggle button's state depending on where the drop happened
   * If close to the top - flip to Close
   * If close to the bottom - flip to Open
   *
   * And recalculate opened ratio for when resizing happens
   */
  const handleDragEnd = (
    _: globalThis.PointerEvent | MouseEvent | TouchEvent,
    _info: PanInfo,
  ) => {
    const height = getMaxDrawerHeight();
    const currentY = drawerRef.current!.getBoundingClientRect().y;

    setPositionsDrawerSettings({
      show: currentY < window.innerHeight,
      ratio: (currentY - height) / height,
      currentHeight: Math.abs(
        Math.min(
          drawerRef.current!.getBoundingClientRect().y,
          window.innerHeight - MIN_DRAWER_HEIGHT,
        ) - window.innerHeight,
      ),
    });

    setIsPositionsDrawerDragged(false);
  };

  const handleDragUpdate = (
    _: globalThis.PointerEvent | MouseEvent | TouchEvent,
    _info: PanInfo,
  ) => {
    adjustContentDimensions();
  };

  const handleAnimationComplete = () => {
    adjustContentDimensions();
  };

  const handleChangeTab = (tab: PositionsDrawerTab) => {
    setPositionsDrawerTab(tab);

    if (Math.abs(top.get() - drawerHeight) < 1) {
      setPositionsDrawerSettings({ show: true });
      animation.start({ y: 0 });
    }
  };

  useEffect(() => {
    const { showRatioPercentage } = calculateShowRatio();

    animation.start({
      y: positionsDrawerSettings.show ? `${showRatioPercentage}%` : "100%",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on show change
  }, [positionsDrawerSettings.show]);

  useEffect(() => {
    handleResize();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- restoring position upon first run
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  return {
    top,
    drawerRef,
    animation,
    dragControls,
    drawerHeight,
    contentHeight,
    positionsDrawerTab,
    positionsDrawerSettings,
    handleDrag,
    handleToggle,
    handleDragEnd,
    handleDragUpdate,
    handleChangeTab,
    handleAnimationComplete,
  };
};
